import { Box } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { deDE } from "@mui/x-data-grid/locales";
import { get } from "lodash";
import React, { FC, useMemo } from "react";
import { useApplication } from "src/context/ApplicationContext";
import { KOSTEN_ARTEN } from "src/lib/constants/constant";
import ApplicationHeading from "src/pages/private/online-antrag/application-heading/ApplicationHeading";
import { DisplayEuroMUI } from "src/utils/formats";
import { enumT } from "src/utils/helper";

// import schemas
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import NachweisPositionSchema from "src/lib/paths/nachweisposition_FULL_Paths.json";
import { belongsToMittelabrufOrganization } from "src/pages/private/online-antrag/structure/mitelabruf/loewe/content/LoeweMittelabrufOrganizations";
const P = MittelabrufSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;

const PersonalausgabenUebersichtLoeweMA: FC<{
  organizationId: number | string;
  kostentraeger: string;
  hideHeading?: boolean;
}> = ({ organizationId, kostentraeger, hideHeading = false }) => {
  const { application } = useApplication();

  const columns = useMemo(() => {
    let column = [
      {
        field: "status",
        headerName: "Status",
        width: 280,
      },
      { field: "zweckbestimmung", headerName: "Zweckbestimmung", width: 250 },
      {
        field: "anzahlStunden",
        headerName: "Anzahl Stunden",
        flex: 1,
        type: "number" as const,
      },
      {
        field: "stundensatz",
        headerName: "Stundensatz",
        flex: 1,
        type: "number" as const,
        valueFormatter: (value: any) => {
          const kostenValue = isNaN(Number(value)) ? 0 : Number(value);
          return DisplayEuroMUI(kostenValue);
        },
      },
      {
        field: "gesamtKosten",
        headerName: "zuwendungsfähige Ausgaben",
        width: 250,
        type: "number" as const,
        valueFormatter: (value: any) => {
          const kostenValue = isNaN(Number(value)) ? 0 : Number(value);
          return DisplayEuroMUI(kostenValue);
        },
      },
    ];
    return column;
  }, []);

  const rows = useMemo(() => {
    let row = get(application, P.Mittelabruf_Nachweispositions.path, [])
      .filter(
        (ele: any) =>
          get(ele, NP_P.Allgemein_Kostenart_Finanzierung.path) ===
            KOSTEN_ARTEN.Personalkosten &&
          belongsToMittelabrufOrganization(ele, organizationId, kostentraeger),
      )
      .map((ele: any) => {
        return {
          id: get(ele, "id"),
          status: enumT(get(ele, NP_P.Allgemein_Status.path)),
          zweckbestimmung: get(ele, NP_P.Allgemein_Bezeichnung.path, ""),
          datumRechnung: get(ele, NP_P.Allgemein_Datum_Der_Rechnung.path),
          datumZahlung: get(ele, NP_P.Allgemein_Datum_Der_Zahlung.path),
          anzahlStunden: Number(
            get(ele, NP_P.Stundennachweis_Gesamtstunden.path, 0),
          ),
          stundensatz: Number(
            get(ele, NP_P.Stundennachweis_Stundensatz.path, 0),
          ),
          gesamtKosten: Number(
            get(ele, NP_P.Allgemein_Ausgaben_Kosten.path, 0),
          ),
        };
      });

    return row;
  }, [application, organizationId, kostentraeger]);

  const initialState: any = useMemo(() => {
    return {
      aggregation: {
        model: {
          stundensatz: "sum",
          gesamtKosten: "sum",
        },
      },
      columns: {
        columnVisibilityModel: {
          // Hide the column used for leaves
          status: false,
        },
      },
      rowGrouping: {
        model: ["status"],
      },
    };
  }, []);

  return (
    <Box sx={{ height: "auto", width: "100%" }}>
      {!hideHeading && (
        <ApplicationHeading title="Personalausgaben Übersicht" />
      )}
      <DataGridPremium
        rows={rows}
        columns={columns}
        initialState={initialState}
        hideFooter
        disableRowSelectionOnClick
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
      />
    </Box>
  );
};
export default PersonalausgabenUebersichtLoeweMA;
